/**
 * @fileoverview New Firm Contact dialog
 *
 * Purpose: Admin-only form for adding a curated firm contact (bank,
 * regulator, professional, or other). Org member rows are auto-created
 * from `orgMemberships` so they aren't an option in the category select.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, Plus, Tag } from "lucide-react";
import { roleIconComponent } from "@/components/settings/tasks/role-icons";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  CONTACT_TYPE_META,
  CONTACT_TYPE_ORDER,
  CREATABLE_FIRM_CATEGORIES,
  FIRM_CATEGORY_META,
} from "./types";
import type { ContactType, FirmContactCategory } from "./types";

interface NewFirmContactDialogProps {
  orgId: Id<"organizations">;
}

export function NewFirmContactDialog({ orgId }: NewFirmContactDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState<FirmContactCategory>("bank");
  /** Selected custom category id (MOR-914), or null for a built-in. */
  const [customCategoryId, setCustomCategoryId] = useState<string | null>(null);
  const [contactType, setContactType] = useState<ContactType>("organisation");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [abn, setAbn] = useState("");
  const [acn, setAcn] = useState("");
  const [notes, setNotes] = useState("");

  const createContact = useConvexMutation(
    api.contacts.mutations.createFirmContact,
  );

  const { data: customCategories } = useQuery({
    ...convexQuery(api.contacts.customCategories.listFirmContactCategories, {
      orgId,
    }),
    enabled: open,
  });

  function reset() {
    setDisplayName("");
    setCategory("bank");
    setCustomCategoryId(null);
    setContactType("organisation");
    setEmail("");
    setPhone("");
    setWebsite("");
    setAbn("");
    setAcn("");
    setNotes("");
  }

  async function handleSubmit() {
    if (!displayName.trim()) {
      toast.error("Display name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await createContact({
        orgId,
        input: {
          displayName: displayName.trim(),
          // A custom category keeps "other" as the built-in fallback so all
          // legacy read paths stay valid.
          category: customCategoryId ? "other" : category,
          customCategoryId: customCategoryId
            ? (customCategoryId as Id<"firmContactCategories">)
            : undefined,
          contactType,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          website: website.trim() || undefined,
          abn: abn.replace(/\s/g, "") || undefined,
          acn: acn.replace(/\s/g, "") || undefined,
          notes: notes.trim() || undefined,
        },
      });
      toast.success("Firm contact created.");
      reset();
      setOpen(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : undefined;
      toast.error(message || "Could not create firm contact.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          New Firm Contact
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Firm Contact</DialogTitle>
          <DialogDescription>
            Add a record to the firm-level directory. Job contacts that match
            this entry by ABN, ACN, or normalised name will auto-link on the
            next reconcile.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Display name *">
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Westpac, ATO, …"
                autoFocus
              />
            </FieldRow>
            <FieldRow label="Category">
              <Select
                value={
                  customCategoryId ? `custom:${customCategoryId}` : category
                }
                onValueChange={(v) => {
                  if (v.startsWith("custom:")) {
                    setCustomCategoryId(v.slice("custom:".length));
                  } else {
                    setCustomCategoryId(null);
                    setCategory(v as FirmContactCategory);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREATABLE_FIRM_CATEGORIES.map((c) => {
                    const meta = FIRM_CATEGORY_META[c];
                    return (
                      <SelectItem key={c} value={c}>
                        <span className="inline-flex items-center gap-2">
                          <meta.icon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                  {(customCategories ?? []).map((c) => {
                    const Icon = roleIconComponent(c.icon) ?? Tag;
                    return (
                      <SelectItem key={c._id} value={`custom:${c._id}`}>
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5" />
                          {c.name}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FieldRow>
          </div>
          <FieldRow label="Contact type">
            <Select
              value={contactType}
              onValueChange={(v) => setContactType(v as ContactType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_TYPE_ORDER.map((t) => {
                  const meta = CONTACT_TYPE_META[t];
                  return (
                    <SelectItem key={t} value={t}>
                      <span className="inline-flex items-center gap-2">
                        <meta.icon className="h-3.5 w-3.5" />
                        {meta.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@example.com"
              />
            </FieldRow>
            <FieldRow label="Phone">
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+61 …"
              />
            </FieldRow>
          </div>

          <FieldRow label="Website">
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://…"
            />
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="ABN">
              <Input
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                placeholder="11 digits"
                className="font-mono"
              />
            </FieldRow>
            <FieldRow label="ACN">
              <Input
                value={acn}
                onChange={(e) => setAcn(e.target.value)}
                placeholder="9 digits"
                className="font-mono"
              />
            </FieldRow>
          </div>

          <FieldRow label="Notes">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context for this contact (visible across all jobs)."
              className="min-h-[80px]"
            />
          </FieldRow>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              "Create contact"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
